import { useContext, useState, useRef } from "react"
import { AuthContext } from "../contexts/user-context/user-context"
import { CriticalOrderBalance } from "../interfaces/delivery-company/orders";
import axios from "axios";
import { API_ENDPOINT } from "../configurations/global.config";

export const useCriticalOrders = () => {
    const { config } = useContext(AuthContext);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [balance, setBalance] = useState<CriticalOrderBalance>()
    
    // Cache to store balance data by merchant ID
    const balanceCache = useRef<Map<string, CriticalOrderBalance>>(new Map());
    // Track ongoing requests to prevent duplicate API calls
    const ongoingRequests = useRef<Map<string, Promise<CriticalOrderBalance | null>>>(new Map());

    const fetchBalance = async (merchantId?: string | number) => {
        // Create cache key - use 'current_user' for requests without merchantId
        const cacheKey = merchantId ? merchantId.toString() : 'current_user';
        
        // Check if we have cached data
        if (balanceCache.current.has(cacheKey)) {
            const cachedBalance = balanceCache.current.get(cacheKey)!;
            setBalance(cachedBalance);
            return cachedBalance;
        }

        // Check if there's already an ongoing request for this merchant
        if (ongoingRequests.current.has(cacheKey)) {
            return ongoingRequests.current.get(cacheKey)!;
        }

        setLoadingBalance(true);
        
        // Create the API request promise
        const requestPromise = (async () => {
            try {
                // Build URL with merchant_id parameter if provided (for organization users)
                let url = `${API_ENDPOINT}/api/v1/merchants/critical-order-balance/`;
                if (merchantId) {
                    url += `?merchant_id=${merchantId}`;
                }

                const response = await axios.get<CriticalOrderBalance>(url, config);
                const balanceData = response.data;
                
                // Cache the result
                balanceCache.current.set(cacheKey, balanceData);
                setBalance(balanceData);
                
                return balanceData;
            } catch (e) {
                console.error('Failed to fetch critical order balance:', e);
                return null;
            } finally {
                setLoadingBalance(false);
                // Remove from ongoing requests
                ongoingRequests.current.delete(cacheKey);
            }
        })();

        // Store the ongoing request
        ongoingRequests.current.set(cacheKey, requestPromise);
        
        return requestPromise;
    };

    // Function to clear cache for a specific merchant or all cache
    const clearCache = (merchantId?: string | number) => {
        if (merchantId) {
            const cacheKey = merchantId.toString();
            balanceCache.current.delete(cacheKey);
        } else {
            balanceCache.current.clear();
        }
    };

    return {
        fetchBalance,
        loadingBalance,
        balance,
        clearCache,
    }

}
